import { REALTIME_BQ_PROJECT_ID } from '../config/api';

// Realtime data is split per GCP project — poc (dev) vs delivery (prod) — while
// Firestore map metadata is shared across both. Each saved area carries a
// `bqProjectId`: the project whose BigQuery holds its realtime data. When that's
// set and doesn't match the project THIS env reads from, the data is physically
// unreachable here, so realtime UIs grey the area out.
//
// TOLERANT OF MISSING: an absent bqProjectId is treated as "this env". Delivery
// doesn't stamp new maps at creation yet (the #425 stamp is only on main), so an
// unstamped map is NOT necessarily wrong-env — only a present, mismatched stamp
// is a hard signal. Backfilling existing maps (see backfill-bqprojectid.mjs)
// makes "missing" mean "no realtime data anywhere", which is correctly benign.
// See docs/projects-persistence.md + card #425.

const PROJECT_ENV_LABEL: Record<string, string> = {
  'telcofy-norway-poc': 'Dev',
  'telcofy-norway-delivery': 'Production',
};

/** True when this env can read the area's realtime data (or the stamp is absent). */
export function realtimeDataIsHere(bqProjectId?: string | null): boolean {
  return !bqProjectId || bqProjectId === REALTIME_BQ_PROJECT_ID;
}

/**
 * Human label for the environment that actually owns the area's realtime data.
 * Only meaningful when `realtimeDataIsHere()` is false.
 */
export function realtimeEnvLabel(bqProjectId?: string | null): string {
  return (bqProjectId && PROJECT_ENV_LABEL[bqProjectId]) || 'another environment';
}

/**
 * User-data (projects) lives in the shared Firestore, so projects are visible in
 * every environment. Classify a project by where its member areas' realtime data
 * lives: it's relevant HERE if at least one member is readable here (missing
 * stamp counts as here — tolerant). A project whose members are ALL cross-env has
 * no readable data here, so realtime surfaces hide it. Empty projects show.
 *
 * This is the frontend-only inference (approach A); the durable version stamps
 * `bqProjectId` on the project doc at creation (backend, like maps #425).
 */
export function projectInThisEnv(
  areaIds: string[],
  bqProjectIdByAreaId: Map<string, string | undefined>
): boolean {
  if (!areaIds.length) return true;
  return areaIds.some((id) => realtimeDataIsHere(bqProjectIdByAreaId.get(id)));
}

/** Where an area's realtime data is: readable in this environment, or only in another one. */
export type RealtimeAvailability = 'here' | 'elsewhere';

/**
 * The one question shared surfaces (Home, Dashboards) may ask about realtime data. They never read
 * the env stamp themselves; only the realtime and emergency pages use realtimeDataIsHere.
 */
export function realtimeAvailability(area: { bqProjectId?: string | null }): RealtimeAvailability {
  return realtimeDataIsHere(area.bqProjectId) ? 'here' : 'elsewhere';
}

/** Same question for a project, from its member areas (see projectInThisEnv). */
export function projectRealtimeAvailability(
  areaIds: string[],
  bqProjectIdByAreaId: Map<string, string | undefined>
): RealtimeAvailability {
  return projectInThisEnv(areaIds, bqProjectIdByAreaId) ? 'here' : 'elsewhere';
}

/**
 * The environment that holds a project's realtime data, when none of it is readable here; null
 * otherwise. Drives the muted "Real Time data in Production" hint on a project card.
 */
export function projectDataElsewhere(
  areaIds: string[],
  bqProjectIdByAreaId: Map<string, string | undefined>
): string | null {
  if (projectRealtimeAvailability(areaIds, bqProjectIdByAreaId) === 'here') return null;
  return realtimeEnvLabel(bqProjectIdByAreaId.get(areaIds[0]));
}
